import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";

const relTime = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Recent drift auto-nudges (green→red weekly drops). Renders as a collapsible
// section on /platform so the platform owner can see at a glance which tenants
// the cron already texted vs which still need a human touch.
export default function DriftAlertHistory({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/platform/drift-alerts/recent?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load drift history"))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || err || !data) return null;
  const alerts = data.alerts || [];

  return (
    <Card className="mt-6 overflow-hidden" data-testid="drift-alert-history">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-colors"
        data-testid="drift-alert-history-toggle"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <div className="font-display text-lg font-bold">Recent drift alerts</div>
          <span className={`text-xs rounded-full px-2 py-0.5 font-mono-label tracking-widest ${alerts.length > 0 ? "bg-red-500/15 text-red-700 border border-red-500/40" : "bg-emerald-500/15 text-emerald-700 border border-emerald-500/40"}`}>
            {alerts.length} in last {data.days}d
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="overflow-x-auto">
          {alerts.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No drift alerts fired in the last {data.days} days — every tenant kept their weekly health above 40 or already recovered.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="drift-alert-history-table">
              <thead className="bg-muted/20 text-[0.65rem] font-mono-label tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Dealer</th>
                  <th className="text-right px-3 py-2.5">Last wk</th>
                  <th className="text-right px-3 py-2.5">This wk</th>
                  <th className="text-right px-3 py-2.5">Δ</th>
                  <th className="text-left px-3 py-2.5">Auto-SMS</th>
                  <th className="text-left px-3 py-2.5">Fired</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {alerts.map((a, i) => {
                  const sms = a.sms || {};
                  const provider = sms.provider || "—";
                  const status = sms.status || "—";
                  const ok = provider === "twilio" && status === "sent";
                  return (
                    <tr key={`${a.tenant_id}-${a.sent_at}-${i}`} data-testid={`drift-alert-row-${a.tenant_id}`} className="hover:bg-muted/20">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-navy">{a.tenant_name}</div>
                        <div className="text-[0.65rem] font-mono text-muted-foreground/70">{a.tenant_id}</div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-700">{a.last_wk}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-red-700">{a.this_wk}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-red-700 font-mono-label">{a.delta}</td>
                      <td className="px-3 py-3 text-xs">
                        {provider === "skipped" ? (
                          <span className="text-muted-foreground">skipped · {status}</span>
                        ) : (
                          <span className={ok ? "text-emerald-700" : "text-amber-700"}>
                            {provider} · {status}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{relTime(a.sent_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
